import { useEffect, useState } from "react";
import { GrDialog } from "@jnpmedi/grebob";
import { nl2br } from "@newLibs/nl2br";
import { MvnMessage } from "@newLibs/utils";
import { useRefreshSession } from "@newHooks/auth/useRefreshSession";
import { isInvalidCredential, useSignOut } from "@newHooks/auth/useSignOut";
import { useUpdateExpiryAt } from "@newHooks/auth/useUpdateExpiryAt";
import { useBoolean } from "@newHooks/common/useBoolean";
import { useInterval } from "@newHooks/common/useInterval";
import { notify } from "@newHooks/context/snackbar/notify";
import { useAwakeAuthTokenMutation } from "@newHooks/mutation/auth/awakeAuthToken.mutation";
import { useRefreshAuthTokenMutation } from "@newHooks/mutation/auth/refreshAuthToken.mutation";
import { Timer } from "./Timer";
import { Waken } from "../../newComponents/header/token/Waken";
import { AUTH_USER_LANG } from "@maven-msg/lang/auth";
import { COMMON_LANG } from "@maven-msg/lang/common";
import { MavenService } from "@maven-rest/common/common.types";

const HEART_BEAT_INTERVAL = 1000 * 60; // 1m
const CRITERIA_SECS = 60 * 15; // 15m

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const DISABLE_TOKEN_REFRESH_SECS = 10; // 10s

export const KICKOUT_CONDITION_ERROR_CODES = ["CREDENTIAL-INVALID", "CREDENTIAL-INVALID-BY-NEW-TOKEN"];

export function DocsAuthTokenManager() {
  const refreshSession = useRefreshSession();
  const updateExpiryAt = useUpdateExpiryAt();
  const signOut = useSignOut();

  const [id, setId] = useState<number>(new Date().getTime());
  const [secs, setSecs] = useState<number>(0);
  const [ignoredId, setIgnoredId] = useState<number>(0);
  const openDialog = useBoolean(false);

  const { mutate: awakeAuthTokenMutation } = useAwakeAuthTokenMutation({
    onSuccess: (data) => {
      updateExpiryAt(data.authToken.expiryAt);
    },
    onError: (error) => {
      if (isInvalidCredential(error)) {
        signOut();
      }
    },
  });

  const { mutate: refreshAuthTokenMutation, isLoading: isRefreshAuthTokenLoading } = useRefreshAuthTokenMutation({
    onSuccess: (data) => {
      const { token, expiryAt } = data.authToken;
      refreshSession({ token, expiryAt });
      setId(new Date().getTime());

      notify(MvnMessage(AUTH_USER_LANG.USER_TOKEN_REFRESH_SUCC), "SUCC");
    },
    onError: (error) => {
      if (isInvalidCredential(error)) {
        signOut();
      }

      notify(MvnMessage(AUTH_USER_LANG.USER_TOKEN_REFRESH_FAIL), "ERROR");
    },
  });

  const handleAwakeAuthToken = () => {
    awakeAuthTokenMutation({ service: MavenService.MavenDocs });
  };

  const handleRefreshAuthToken = () => {
    refreshAuthTokenMutation({ service: MavenService.MavenDocs });
  };

  useInterval(() => {
    handleAwakeAuthToken();
  }, HEART_BEAT_INTERVAL);

  useEffect(() => {
    handleAwakeAuthToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <GrDialog
        title={MvnMessage(AUTH_USER_LANG.USER_TOKEN_EXPIRY_SESSION)}
        size="xs"
        onConfirm={() => {
          handleRefreshAuthToken();
          openDialog.onFalse();
          setIgnoredId(id);
        }}
        onClose={() => {
          openDialog.onFalse();
          setIgnoredId(id);
        }}
        open={openDialog.value}
        confirmButton={{ children: MvnMessage(COMMON_LANG.EXTEND) }}
        cancelButton={{ children: MvnMessage(COMMON_LANG.CANCEL_ALTER) }}
      >
        {nl2br(
          `${MvnMessage(AUTH_USER_LANG.USER_TOKEN_EXPIRY_ALERT, { minutes: Math.floor(secs / 60), seconds: secs % 60 })}
          ${MvnMessage(AUTH_USER_LANG.USER_TOKEN_EXTEND_CONFIRM)}`
        )}
      </GrDialog>

      <Timer
        awakeRequest={handleAwakeAuthToken}
        refreshRequest={handleRefreshAuthToken}
        busy={isRefreshAuthTokenLoading}
        disabled={isRefreshAuthTokenLoading}
        onTick={(secs) => {
          setSecs(secs);

          if (id !== ignoredId) {
            openDialog.setValue(secs > 0 && secs <= CRITERIA_SECS);
          }
        }}
        defaultTitle={MvnMessage(AUTH_USER_LANG.USER_TOKEN_EXTEND_BTN_DESC)}
      />
      <Waken />
    </>
  );
}
